import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Mail, Power, Trash2, X, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const req = (method, path, data) => axios({ method, url: `${API}${path}`, data, withCredentials: true });
const input =
  "w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30";
const btnDark =
  "inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2 text-xs font-semibold";
const btnGhost =
  "inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 text-xs";

function ManualEnrollForm({ courses, tiers, onDone, onCancel }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    package_id: "basic",
    payment_method: "QR",
    amount: 4999,
    payment_date: new Date().toISOString().slice(0, 10),
    transaction_id: "",
    notes: "",
    courses: [],
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await req("post", "/admin/students/manual-enroll", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        package_id: form.package_id,
        courses: form.courses,
        payment_method: form.payment_method,
        amount_paise: Math.round(Number(form.amount) * 100),
        payment_date: form.payment_date,
        transaction_id: form.transaction_id || null,
        notes: form.notes || null,
      });
      toast.success(
        data.email_sent
          ? "Student enrolled successfully. Set-password email sent."
          : `Student enrolled. Email NOT sent (${data.email_error || "email disabled"}). Share this setup link: ${data.activation_url || "—"}`,
        { duration: 12000 },
      );
      if (data.activation_url) {
        try {
          await navigator.clipboard.writeText(data.activation_url);
          toast.info("Set-password link copied to clipboard");
        } catch {
          /* clipboard unavailable */
        }
      }
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not enroll student");
    } finally {
      setBusy(false);
    }
  };

  const toggleCourse = (slug) =>
    setForm((f) => ({
      ...f,
      courses: f.courses.includes(slug) ? f.courses.filter((s) => s !== slug) : [...f.courses, slug],
    }));

  return (
    <form
      onSubmit={submit}
      data-testid="manual-enroll-form"
      className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5 space-y-3 max-w-2xl"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-base font-semibold tracking-tight">Add student manually (QR / offline)</h3>
        <button type="button" onClick={onCancel} className={btnGhost}>
          <X className="h-3 w-3" />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input required minLength={2} data-testid="enroll-name" className={input} placeholder="Student name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input required type="email" data-testid="enroll-email" className={input} placeholder="Student email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input data-testid="enroll-phone" className={input} placeholder="Mobile number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <select data-testid="enroll-package" className={input} value={form.package_id} onChange={(e) => setForm({ ...form, package_id: e.target.value })}>
          {Object.entries(tiers || {}).map(([id, t]) => (
            <option key={id} value={id}>
              {t.name}
            </option>
          ))}
        </select>
        <select data-testid="enroll-method" className={input} value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
          <option value="QR">QR</option>
          <option value="Other">Other</option>
        </select>
        <input required type="number" data-testid="enroll-amount" className={input} placeholder="Amount paid (₹)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        <input type="date" data-testid="enroll-date" className={input} value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} />
        <input data-testid="enroll-txn" className={input} placeholder="Transaction / receipt ID" value={form.transaction_id} onChange={(e) => setForm({ ...form, transaction_id: e.target.value })} />
      </div>
      <textarea rows={2} data-testid="enroll-notes" className={input} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">
          Courses chosen by the student (Premium unlocks everything automatically)
        </div>
        <div className="flex flex-wrap gap-2">
          {(courses || []).map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => toggleCourse(c.slug)}
              data-testid={`enroll-course-${c.slug}`}
              className={`px-3 py-1.5 rounded-full text-[11px] border transition-colors ${
                form.courses.includes(c.slug)
                  ? "bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] border-transparent"
                  : "border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300"
              }`}
            >
              {c.title}
            </button>
          ))}
        </div>
      </div>

      <button type="submit" disabled={busy} data-testid="enroll-submit" className={btnDark}>
        {busy ? "Enrolling…" : "Enroll student & send email"}
      </button>
    </form>
  );
}

export default function StudentsManager() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [pkg, setPkg] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [meta, setMeta] = useState({ courses: [], tiers: {} });
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (pkg) params.set("package", pkg);
    const { data } = await req("get", `/admin/students?${params.toString()}`);
    setRows(data);
  }, [q, pkg]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    req("get", "/admin/packages").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const resend = async (id) => {
    const reset = window.confirm(
      "Also clear their current password so they set a new one from the link?",
    );
    try {
      const { data } = await req("post", `/admin/students/${id}/resend-welcome?reset_password=${reset}`);
      toast[data.sent ? "success" : "error"](
        data.sent
          ? "Email queued for sending"
          : `Email failed: ${data.error}. ${data.activation_url ? `Setup link: ${data.activation_url}` : ""}`,
        { duration: 12000 },
      );
      if (data.activation_url) {
        try {
          await navigator.clipboard.writeText(data.activation_url);
          toast.info("Set-password link copied to clipboard");
        } catch {
          /* clipboard unavailable */
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend");
    }
  };

  const toggleStatus = async (s) => {
    await req("put", `/admin/students/${s.id}`, { status: s.status === "active" ? "suspended" : "active" });
    toast.success(s.status === "active" ? "Account suspended" : "Account reactivated");
    load();
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.email}? This removes their progress permanently.`)) return;
    await req("delete", `/admin/students/${s.id}`);
    toast.success("Student deleted");
    load();
  };

  const openDetail = async (s) => {
    const { data } = await req("get", `/admin/students/${s.id}`);
    setDetail(data);
  };

  return (
    <div data-testid="admin-students-manager" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            data-testid="students-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email or student ID…"
            className={`${input} pl-8`}
          />
        </div>
        <select data-testid="students-filter-package" className={`${input} max-w-[180px]`} value={pkg} onChange={(e) => setPkg(e.target.value)}>
          <option value="">All packages</option>
          {Object.entries(meta.tiers || {}).map(([id, t]) => (
            <option key={id} value={id}>
              {t.name}
            </option>
          ))}
        </select>
        <button type="button" onClick={() => setShowForm((v) => !v)} data-testid="admin-add-student-manually" className={btnDark}>
          <Plus className="h-3.5 w-3.5" /> Add Student Manually
        </button>
      </div>

      {showForm && (
        <ManualEnrollForm
          courses={meta.courses}
          tiers={meta.tiers}
          onCancel={() => setShowForm(false)}
          onDone={() => {
            setShowForm(false);
            load();
          }}
        />
      )}

      <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] overflow-x-auto">
        <table data-testid="admin-students-table" className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 border-b border-slate-100 dark:border-white/5">
              {["Student", "Email", "Student ID", "Package", "Courses", "Enrolled", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-slate-400 text-sm">
                  No students yet.
                </td>
              </tr>
            )}
            {rows.map((s) => (
              <tr key={s.id} data-testid={`student-row-${s.student_id}`} className="hover:bg-slate-50 dark:hover:bg-white/5">
                <td className="px-4 py-3">
                  <button type="button" onClick={() => openDetail(s)} className="font-medium hover:underline">
                    {s.name}
                  </button>
                </td>
                <td className="px-4 py-3 text-slate-500">{s.email}</td>
                <td className="px-4 py-3 text-slate-500">{s.student_id}</td>
                <td className="px-4 py-3">{s.package_name || "—"}</td>
                <td className="px-4 py-3">{(s.courses || []).length}</td>
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                  {s.enrolled_at ? new Date(s.enrolled_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3">
                  <span className={s.status === "active" ? "text-emerald-500" : "text-rose-500"}>{s.status}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 justify-end">
                    <button type="button" onClick={() => resend(s.id)} data-testid={`resend-welcome-${s.student_id}`} className={btnGhost} title="Resend enrollment email">
                      <Mail className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => toggleStatus(s)} data-testid={`toggle-status-${s.student_id}`} className={btnGhost} title="Suspend / activate">
                      <Power className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => remove(s)} data-testid={`delete-student-${s.student_id}`} className={btnGhost} title="Delete">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div data-testid="student-detail-panel" className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-display text-base font-semibold tracking-tight">{detail.student.name}</h3>
              <p className="text-xs text-slate-500">
                {detail.student.email} · {detail.student.student_id} · {detail.student.package_name || "no package"}
              </p>
            </div>
            <button type="button" onClick={() => setDetail(null)} className={btnGhost}>
              <X className="h-3 w-3" />
            </button>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
                Course completion
              </div>
              <ul className="space-y-1.5">
                {detail.courses.length === 0 && <li className="text-slate-400">No courses assigned.</li>}
                {detail.courses.map((c) => (
                  <li key={c.slug} className="flex items-center justify-between gap-3">
                    <span className="truncate">{c.title}</span>
                    <span className="text-slate-500 shrink-0">
                      {c.completed_sessions}/{c.total_sessions} · {c.percent}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
                Enrollments & certificates
              </div>
              <ul className="space-y-1.5">
                {detail.enrollments.map((e) => (
                  <li key={e.id} className="text-slate-500">
                    {e.package_name} · ₹{((e.amount_paise || 0) / 100).toLocaleString("en-IN")} · {e.payment_method}
                  </li>
                ))}
                {detail.certificates.map((c) => (
                  <li key={c.cert_id} className="text-emerald-500">
                    {c.course_title} — {c.cert_id}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
